import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnProps } from 'antd/lib/table';
import { Button, Table } from 'antd';
import { USER_ROLES } from 'constants/users';
import { PERMISSION_ACTIONS, PERMISSION_ROUTES } from 'constants/permissions';
import { PlusOutlined } from '@ant-design/icons';
import { fetchUsers } from 'services/api/users';
import { UserListRow } from 'types/api/users';

import useOrgEffect from 'hooks/useOrgEffect';
import { showErrorMessage } from 'utils/showErrorMessage';
import { PageWrapper } from 'components/ui';
import { Private, OrganizationAlert } from 'components/shared';
import { UsersModal } from '../components';

const PAGE_LIMIT = 10;

const Translators: FC = () => {
  const [isDetailsVisible, setDetailsVisible] = useState(false);
  const [detailsId, setDetailsId] = useState('');
  const [page, setPage] = useState(1);
  const { t } = useTranslation();

  const [results, setResults] = useState<UserListRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  useOrgEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        const res = await fetchUsers({
          page,
          limit: PAGE_LIMIT,
          role: USER_ROLES.translator
        });

        if (cancelled) return;
        setResults(res.results);
        setTotal(res.total);
      } catch (err) {
        if (!cancelled) showErrorMessage(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [page, refreshKey]);

  const refetch = useCallback(() => {
    setRefreshKey(k => k + 1);
  }, []);

  const columns: ColumnProps<UserListRow>[] = useMemo(
    () => [
      {
        title: t('id'),
        dataIndex: 'id',
        key: 'id'
      },
      {
        title: t('table.name'),
        dataIndex: 'name',
        key: 'name',
        render: (_, record) => `${record.first_name} ${record.last_name}`
      },
      {
        title: t('table.email'),
        dataIndex: 'email',
        key: 'email',
        render: value => <a href={`mailto:${value}`}>{value}</a>
      },
      {
        title: t('table.phone'),
        dataIndex: 'phone',
        key: 'phone',
        render: value => <a href={`tel:${value}`}>{value}</a>
      }
      // {
      //   title: '',
      //   dataIndex: 'id',
      //   key: 'id',
      //   width: '5%',
      //   align: 'right',
      //   render: id => (
      //     <Private
      //       route={PERMISSION_ROUTES.translators}
      //       action={PERMISSION_ACTIONS.update}
      //     >
      //       <Button
      //         onClick={() => {
      //           setDetailsVisible(true);
      //           setDetailsId(id);
      //         }}
      //       >
      //         {t('common.edit')}
      //       </Button>
      //     </Private>
      //   )
      // }
    ],
    [t]
  );

  const onDetailsClose = useCallback(() => {
    setDetailsId('');
    setDetailsVisible(false);
  }, []);

  return (
    <PageWrapper
      title={t('translator.page_title')}
      color="white"
      extra={[
        <Private
          key="add-translator"
          route={PERMISSION_ROUTES.translators}
          action={PERMISSION_ACTIONS.create}
        >
          <OrganizationAlert>
            <Button type="primary" onClick={() => setDetailsVisible(true)}>
              <PlusOutlined />
              {t('translator.add')}
            </Button>
          </OrganizationAlert>
        </Private>
      ]}
    >
      <Table
        dataSource={results}
        columns={columns}
        loading={loading}
        rowKey="id"
        pagination={{
          total,
          onChange: p => setPage(p),
          showSizeChanger: false
        }}
      />
      {isDetailsVisible && (
        <UsersModal
          onClose={onDetailsClose}
          role={USER_ROLES.translator}
          id={detailsId}
          refetch={refetch}
        />
      )}
    </PageWrapper>
  );
};

export default Translators;
